import React, { useEffect, useRef, useState } from "react";
import {
  X,
  Ghost,
  MessageSquare,
  Send,
  Mail,
  KeyRound,
  Smartphone,
  Apple,
  Check,
  ArrowRight,
} from "lucide-react";
import { joinWaitlist } from "../api";
import { useToast } from "../hooks/use-toast";

const METHODS = [
  { id: "telegram", label: "Telegram", icon: Send, hint: "@handle" },
  { id: "session", label: "Session", icon: KeyRound, hint: "Session ID" },
  { id: "email", label: "Email", icon: Mail, hint: "you@domain" },
  { id: "other", label: "Other", icon: MessageSquare, hint: "handle + platform" },
];

const PLATFORMS = [
  { id: "ios", label: "iOS", icon: Apple },
  { id: "android", label: "Android", icon: Smartphone },
  { id: "both", label: "Both", icon: Ghost },
];

const PhantomChatWaitlistModal = ({ open, onClose }) => {
  const [method, setMethod] = useState("telegram");
  const [handle, setHandle] = useState("");
  const [platform, setPlatform] = useState("both");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null); // { alreadyOnList: bool }
  const inputRef = useRef(null);
  const { toast } = useToast();

  useEffect(() => {
    if (open) {
      setSuccess(null);
      setTimeout(() => inputRef.current?.focus(), 60);
      const onKey = (e) => e.key === "Escape" && onClose?.();
      window.addEventListener("keydown", onKey);
      return () => window.removeEventListener("keydown", onKey);
    }
  }, [open, onClose]);

  if (!open) return null;

  const activeMethod = METHODS.find((m) => m.id === method);

  const submit = async (e) => {
    e.preventDefault();
    const trimmed = handle.trim();
    if (!trimmed) return;
    setLoading(true);
    try {
      const res = await joinWaitlist({
        contact_method: method,
        contact_handle: trimmed,
        platform,
        note: note.trim() || null,
      });
      setSuccess({ alreadyOnList: !!res.already_on_list });
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Try again.";
      toast({ title: "Couldn't join", description: String(msg) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4"
      data-testid="phantom-chat-waitlist-modal"
    >
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-md"
        onClick={onClose}
      />
      <div className="relative w-full max-w-lg card-glass rounded-3xl border border-white/10 overflow-hidden">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 h-9 w-9 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center text-white/70 hover:text-white transition-colors z-10"
          aria-label="Close"
          data-testid="phantom-chat-waitlist-close"
        >
          <X className="h-4 w-4" />
        </button>

        {/* Aurora accent */}
        <div className="absolute inset-0 aurora-purple opacity-40 pointer-events-none" />

        <div className="relative p-8">
          {success ? (
            <div className="text-center py-6" data-testid="phantom-chat-waitlist-success">
              <div className="mx-auto h-14 w-14 rounded-full bg-purple-500/15 border border-purple-500/40 flex items-center justify-center">
                <Check className="h-6 w-6 text-purple-300" />
              </div>
              <h3 className="mt-5 font-headline text-2xl font-bold text-white">
                {success.alreadyOnList
                  ? "You were already on the list."
                  : "You're on the list."}
              </h3>
              <p className="mt-3 text-sm text-white/60 leading-relaxed max-w-sm mx-auto">
                We'll ping <span className="font-mono text-purple-300">{handle}</span>{" "}
                on <span className="text-white/90 capitalize">{activeMethod.label}</span>{" "}
                the moment Phantom X is live. No newsletters. No cross-sells. One
                message, one time.
              </p>
              <button
                onClick={onClose}
                className="btn-primary mt-8"
                data-testid="phantom-chat-waitlist-done"
              >
                Nice. Close <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <form onSubmit={submit}>
              <div className="chip">
                <MessageSquare className="h-3 w-3" /> Phantom X waitlist
              </div>
              <h3 className="mt-4 font-headline text-3xl font-bold text-gradient-purple leading-tight">
                Join the waitlist.<br />No email required.
              </h3>
              <p className="mt-3 text-sm text-white/60 leading-relaxed">
                Give us one way to reach you. We'll drop a launch message and
                delete the entry after.
              </p>

              {/* Method picker */}
              <div className="mt-6">
                <div className="text-xs uppercase tracking-[0.2em] text-white/40 mb-2">
                  How should we reach you?
                </div>
                <div className="grid grid-cols-4 gap-2">
                  {METHODS.map((m) => {
                    const Icon = m.icon;
                    const active = method === m.id;
                    return (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => setMethod(m.id)}
                        className={`px-2 py-3 rounded-xl border text-xs font-display font-semibold flex flex-col items-center gap-1.5 transition-colors ${
                          active
                            ? "bg-purple-500/15 border-purple-500/50 text-white"
                            : "bg-white/[0.02] border-white/10 text-white/60 hover:text-white hover:border-white/20"
                        }`}
                        data-testid={`waitlist-method-${m.id}`}
                      >
                        <Icon className="h-4 w-4" />
                        {m.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Handle input */}
              <label className="block mt-5">
                <div className="text-xs uppercase tracking-[0.2em] text-white/40 mb-2">
                  Your {activeMethod.label} <span className="text-white/30">— {activeMethod.hint}</span>
                </div>
                <input
                  ref={inputRef}
                  value={handle}
                  onChange={(e) => setHandle(e.target.value)}
                  required
                  maxLength={200}
                  placeholder={
                    activeMethod.id === "telegram" ? "@yourhandle"
                    : activeMethod.id === "email" ? "burner@proton.me"
                    : activeMethod.id === "session" ? "05f5…"
                    : "how we should reach you"
                  }
                  className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white font-mono text-sm outline-none focus:border-purple-500/50 focus:bg-purple-500/5 transition-colors"
                  data-testid="waitlist-handle-input"
                />
              </label>

              {/* Platform picker */}
              <div className="mt-5">
                <div className="text-xs uppercase tracking-[0.2em] text-white/40 mb-2">
                  Which app store do you want it on?
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {PLATFORMS.map((p) => {
                    const Icon = p.icon;
                    const active = platform === p.id;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => setPlatform(p.id)}
                        className={`px-3 py-2.5 rounded-xl border text-xs font-display font-semibold flex items-center justify-center gap-2 transition-colors ${
                          active
                            ? "bg-purple-500/15 border-purple-500/50 text-white"
                            : "bg-white/[0.02] border-white/10 text-white/60 hover:text-white hover:border-white/20"
                        }`}
                        data-testid={`waitlist-platform-${p.id}`}
                      >
                        <Icon className="h-3.5 w-3.5" />
                        {p.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Optional note */}
              <label className="block mt-5">
                <div className="text-xs uppercase tracking-[0.2em] text-white/40 mb-2">
                  Anything you want us to know? <span className="text-white/30">(optional)</span>
                </div>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  maxLength={500}
                  rows={2}
                  placeholder="e.g. bundle a Phantom Key with my Pixel 10 order"
                  className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white text-sm outline-none focus:border-purple-500/50 focus:bg-purple-500/5 transition-colors resize-none"
                  data-testid="waitlist-note-input"
                />
              </label>

              {/* Submit */}
              <div className="mt-6 flex items-center justify-between gap-3 flex-wrap">
                <div className="text-[11px] text-white/40 leading-snug max-w-[220px]">
                  We don't verify your handle. Use a burner — it works.
                </div>
                <button
                  type="submit"
                  disabled={loading || !handle.trim()}
                  className="btn-primary disabled:opacity-50"
                  data-testid="waitlist-submit-btn"
                >
                  {loading ? "Joining…" : (
                    <>Join the waitlist <ArrowRight className="h-4 w-4" /></>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default PhantomChatWaitlistModal;
